<template>
  <div class="bottom-panel" :class="{ 'collapsed': !visible }"
    :style="{ height: panelHeight + 'px', left: (leftOffset + leftDragOffset) + 'px', right: (rightOffset + rightDragOffset) + 'px' }">
    <!-- Resize Handle -->
    <div class="resize-handle" @mousedown="startResize" @touchstart.prevent="startResizeTouch" title="拖拽调整高度"></div>
    <!-- Horizontal Resize Handles -->
    <div class="resize-handle-x left" @mousedown="(e) => startResizeH('left', e)"
      @touchstart.prevent="(e) => startResizeHTouch('left', e)" title="拖拽调整左侧宽度"></div>
    <div class="resize-handle-x right" @mousedown="(e) => startResizeH('right', e)"
      @touchstart.prevent="(e) => startResizeHTouch('right', e)" title="拖拽调整右侧宽度"></div>

    <!-- Panel Header -->
    <div class="panel-header">
      <div class="header-left">
        <LazyIcon name="logIcon" :size="18" style="margin-right: 8px;" />
        <div class="panel-title">日志面板</div>
        <div class="header-tools">
          <ElInput v-model="searchText" class="search-input" placeholder="过滤日志..." clearable />
          <ElSelect v-model="logFilter" class="log-filter" placeholder="所有级别" popper-class="bottom-panel-level-popper">
            <template #prefix>
              <ElIcon>
                <component :is="currentLevelIcon" />
              </ElIcon>
            </template>
            <ElOption label="所有级别" value="">
              <ElIcon><Collection /></ElIcon>
              <span>所有级别</span>
            </ElOption>
            <ElOption label="信息" value="info">
              <ElIcon><Document /></ElIcon>
              <span>信息</span>
            </ElOption>
            <ElOption label="警告" value="warn">
              <ElIcon><QuestionFilled /></ElIcon>
              <span>警告</span>
            </ElOption>
            <ElOption label="错误" value="error">
              <ElIcon><CircleClose /></ElIcon>
              <span>错误</span>
            </ElOption>
            <ElOption label="调试" value="debug">
              <ElIcon><Setting /></ElIcon>
              <span>调试</span>
            </ElOption>
          </ElSelect>
        </div>
      </div>

      <div class="header-actions">
        <button class="header-btn" title="清空日志" @click="clearLogs">
          清空日志
        </button>
        <div class="header-controls">
          <ElDropdown trigger="click" placement="top-end">
            <button class="header-btn dropdown-trigger" title="更多">
              <ElIcon>
                <MoreFilled />
              </ElIcon>
            </button>
            <template #dropdown>
              <ElDropdownMenu>
                <ElDropdownItem @click="exportLogs">
                  <ElIcon>
                    <FolderOpened />
                  </ElIcon>
                  导出日志
                </ElDropdownItem>
              </ElDropdownMenu>
            </template>
          </ElDropdown>
        </div>
      </div>
    </div>

    <!-- Panel Content -->
    <div class="panel-content" v-if="visible">
      <div class="logs-container">
        <div class="logs-list" ref="logsList">
          <div v-for="(log, index) in filteredLogs" :key="index" class="log-item" :class="log.level"
            @click="openLogDetail(log)">
            <span class="log-bracket" aria-hidden="true"></span>
            <span class="log-time">{{ formatTime(log.timestamp) }}</span>
            <span class="log-level">{{ log.level.toUpperCase() }}</span>
            <span class="log-node" v-if="log.nodeId" @click.stop="locateNode(log.nodeId)">{{ log.nodeLabel || log.nodeId
            }}</span>
            <span class="log-message">{{ log.message }}</span>
            <span class="log-json" v-if="log.jsonData">{{ formatJsonPreview(log.jsonData) }}</span>
          </div>

          <div v-if="filteredLogs.length === 0" class="no-logs">
            <div class="no-logs-icon">
              <ElIcon>
                <Document />
              </ElIcon>
            </div>
            <div class="no-logs-text">{{ logFilter ? '当前级别无日志记录' : '暂无日志记录' }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Log Detail Modal -->
    <ElDialog v-model="logDetailVisible" title="日志详情" width="60%" :modal="true">
      <div class="log-detail-content" v-if="selectedLog">
        <div class="detail-row">
          <label>时间:</label>
          <span>{{ formatTime(selectedLog.timestamp) }}</span>
        </div>
        <div class="detail-row">
          <label>级别:</label>
          <span :class="`level-${selectedLog.level}`">{{ selectedLog.level.toUpperCase() }}</span>
        </div>
        <div class="detail-row" v-if="selectedLog.nodeId">
          <label>节点:</label>
          <span class="node-link" @click="locateNode(selectedLog.nodeId)">
            {{ selectedLog.nodeLabel || selectedLog.nodeId }}
          </span>
        </div>
        <div class="detail-row" v-if="selectedLog.source">
          <label>来源:</label>
          <span>{{ selectedLog.source }}</span>
        </div>
        <div class="detail-row">
          <label>消息:</label>
          <span>{{ selectedLog.message }}</span>
        </div>
        <div class="detail-row" v-if="selectedLog.jsonData">
          <label>JSON数据:</label>
          <pre class="json-display">{{ JSON.stringify(selectedLog.jsonData, null, 2) }}</pre>
        </div>
      </div>
    </ElDialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onUnmounted, inject } from 'vue'
import { useWorkflowStore } from 'mflows'
import { ElDropdown, ElDropdownMenu, ElDropdownItem, ElDialog, ElIcon, MoreFilled, Delete, FolderOpened, Document, ElSelect, ElOption, ElInput, Collection, QuestionFilled, CircleClose, Setting } from '@/plugins/elementPlus'
import { LazyIcon } from '@/exports/components'

const props = defineProps<{
  visible: boolean
  leftOffset?: number
  rightOffset?: number
}>()

const emit = defineEmits<{
  toggle: []
  'update:height': [height: number]
}>()

// 注入工作流store
const workflow = useWorkflowStore()

// 面板高度管理 + 宽度拖拽偏移
const panelHeight = ref(240)
const minHeight = 60
const maxHeight = ref(getMaxHeight())
const isResizing = ref(false)

// 水平拖拽偏移（在传入的leftOffset/rightOffset基础上叠加）
const leftDragOffset = ref(0)
const rightDragOffset = ref(0)
const minPanelWidth = 360

function getMaxHeight() {
  // 预留顶部工具栏/间距 80px，确保不挡住上方内容
  return Math.max(120, window.innerHeight - 80)
}

function getCurrentWidth() {
  return window.innerWidth - (leftOffset.value + leftDragOffset.value) - (rightOffset.value + rightDragOffset.value)
}

function clampHorizontal() {
  // 确保最小宽度并避免负偏移
  leftDragOffset.value = Math.max(0, leftDragOffset.value)
  rightDragOffset.value = Math.max(0, rightDragOffset.value)
  let width = getCurrentWidth()
  if (width < minPanelWidth) {
    // 优先回退最近一次拖动的方向由调用处负责，这里等分回退
    const deficit = minPanelWidth - width
    // 尝试同时回退两边（不小于0）
    const leftCan = Math.min(leftDragOffset.value, Math.ceil(deficit / 2))
    leftDragOffset.value -= leftCan
    const remain = deficit - leftCan
    const rightCan = Math.min(rightDragOffset.value, remain)
    rightDragOffset.value -= rightCan
  }
}

// 使用传入的偏移，若未传则默认为 0
const leftOffset = computed(() => props.leftOffset ?? 0)
const rightOffset = computed(() => props.rightOffset ?? 0)

// 扩展日志数据结构
interface LogEntry {
  timestamp: Date
  level: 'info' | 'warn' | 'error' | 'debug'
  message: string
  source?: string
  nodeId?: string
  nodeLabel?: string
  jsonData?: any
}

// 日志管理
const logs = ref<LogEntry[]>([])
const logFilter = ref('')
const levelIconMap = {
  '': Collection,
  info: Document,
  warn: QuestionFilled,
  error: CircleClose,
  debug: Setting
}
const currentLevelIcon = computed(() => levelIconMap[logFilter.value as keyof typeof levelIconMap] || Collection)

// 日志详情对话框
const logDetailVisible = ref(false)
const selectedLog = ref<LogEntry | null>(null)

const searchText = ref('')
const filteredLogs = computed(() => {
  const levelOk = (log: LogEntry) => !logFilter.value || log.level === logFilter.value
  const q = searchText.value.trim().toLowerCase()
  const textOk = (log: LogEntry) => {
    if (!q) return true
    const pack = [log.message, log.source, log.nodeLabel, log.nodeId && String(log.nodeId)].filter(Boolean).join(' ').toLowerCase()
    return pack.includes(q)
  }
  return logs.value.filter(l => levelOk(l) && textOk(l))
})

const subtitleText = computed(() => `工作流执行日志 · ${filteredLogs.value.length} 条记录`)
// 调整大小功能
function startResize(event: MouseEvent) {
  isResizing.value = true
  const startY = event.clientY
  const startHeight = panelHeight.value

  const handle = (event.currentTarget || document.querySelector('.resize-handle')) as HTMLElement | null
  if (handle) handle.classList.add('resizing')

  document.body.style.cursor = 'ns-resize'
  document.body.style.userSelect = 'none'

  function doResize(event: MouseEvent) {
    const deltaY = startY - event.clientY // 注意：向上拖拽应该增加高度
    const newHeight = Math.min(maxHeight.value, Math.max(minHeight, startHeight + deltaY))
    panelHeight.value = newHeight
    emit('update:height', newHeight)
    localStorage.setItem('bottomPanelHeight', newHeight.toString())
  }

  function stopResize() {
    isResizing.value = false
    document.removeEventListener('mousemove', doResize)
    document.removeEventListener('mouseup', stopResize)

    const h = (event.currentTarget || document.querySelector('.resize-handle')) as HTMLElement | null
    if (h) h.classList.remove('resizing')
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }

  document.addEventListener('mousemove', doResize)
  document.addEventListener('mouseup', stopResize)
  event.preventDefault()
}

// 触摸拖拽支持（移动端/触摸屏）
function startResizeTouch(e: TouchEvent) {
  isResizing.value = true
  const startY = e.touches[0].clientY
  const startHeight = panelHeight.value
  const handle = (e.currentTarget || document.querySelector('.resize-handle')) as HTMLElement | null
  if (handle) handle.classList.add('resizing')

  document.body.style.cursor = 'ns-resize'
  document.body.style.userSelect = 'none'

  const doResize = (ev: TouchEvent) => {
    const currentY = ev.touches[0]?.clientY ?? startY
    const deltaY = startY - currentY
    const newHeight = Math.min(maxHeight.value, Math.max(minHeight, startHeight + deltaY))
    panelHeight.value = newHeight
    emit('update:height', newHeight)
    localStorage.setItem('bottomPanelHeight', newHeight.toString())
  }

  const stopResize = () => {
    isResizing.value = false
    document.removeEventListener('touchmove', doResize)
    document.removeEventListener('touchend', stopResize)
    if (handle) handle.classList.remove('resizing')
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }

  document.addEventListener('touchmove', doResize, { passive: false })
  document.addEventListener('touchend', stopResize)
}

// 新增：左右宽度拖拽（鼠标）
function startResizeH(side: 'left' | 'right', event: MouseEvent) {
  const startX = event.clientX
  const startLeft = leftDragOffset.value
  const startRight = rightDragOffset.value
  const handle = event.currentTarget as HTMLElement | null
  if (handle) handle.classList.add('resizing')

  document.body.style.cursor = 'ew-resize'
  document.body.style.userSelect = 'none'

  const doResize = (e: MouseEvent) => {
    const deltaX = e.clientX - startX
    if (side === 'left') {
      let proposed = Math.max(0, startLeft + deltaX)
      leftDragOffset.value = proposed
    } else {
      let proposed = Math.max(0, startRight - deltaX) // 右侧向右拖 => deltaX>0 => 减小right偏移
      rightDragOffset.value = proposed
    }
    clampHorizontal()
    localStorage.setItem('bottomPanelLeftDrag', leftDragOffset.value.toString())
    localStorage.setItem('bottomPanelRightDrag', rightDragOffset.value.toString())
  }

  const stopResize = () => {
    document.removeEventListener('mousemove', doResize)
    document.removeEventListener('mouseup', stopResize)
    if (handle) handle.classList.remove('resizing')
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }

  document.addEventListener('mousemove', doResize)
  document.addEventListener('mouseup', stopResize)
}

// 新增：左右宽度拖拽（触摸）
function startResizeHTouch(side: 'left' | 'right', e: TouchEvent) {
  const startX = e.touches[0].clientX
  const startLeft = leftDragOffset.value
  const startRight = rightDragOffset.value
  const handle = e.currentTarget as HTMLElement | null
  if (handle) handle.classList.add('resizing')

  document.body.style.cursor = 'ew-resize'
  document.body.style.userSelect = 'none'

  const doResize = (ev: TouchEvent) => {
    const currentX = ev.touches[0]?.clientX ?? startX
    const deltaX = currentX - startX
    if (side === 'left') {
      let proposed = Math.max(0, startLeft + deltaX)
      leftDragOffset.value = proposed
    } else {
      let proposed = Math.max(0, startRight - deltaX)
      rightDragOffset.value = proposed
    }
    clampHorizontal()
    localStorage.setItem('bottomPanelLeftDrag', leftDragOffset.value.toString())
    localStorage.setItem('bottomPanelRightDrag', rightDragOffset.value.toString())
  }

  const stopResize = () => {
    document.removeEventListener('touchmove', doResize)
    document.removeEventListener('touchend', stopResize)
    if (handle) handle.classList.remove('resizing')
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }

  document.addEventListener('touchmove', doResize, { passive: false })
  document.addEventListener('touchend', stopResize)
}

// 添加日志 - 支持JSON和节点信息
function addLog(
  level: 'info' | 'warn' | 'error' | 'debug',
  message: string,
  options?: {
    source?: string
    nodeId?: string
    nodeLabel?: string
    jsonData?: any
  }
) {
  logs.value.push({
    timestamp: new Date(),
    level,
    message,
    source: options?.source,
    nodeId: options?.nodeId,
    nodeLabel: options?.nodeLabel,
    jsonData: options?.jsonData
  })
}

// 清空日志
function clearLogs() {
  logs.value = []
}

// 导出日志
function exportLogs() {
  const data = JSON.stringify(logs.value, null, 2)
  const blob = new Blob([data], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `workflow-logs-${new Date().toISOString().slice(0, 10)}.json`
  a.click()
  URL.revokeObjectURL(url)
}

// 格式化时间
function formatTime(date: Date): string {
  return date.toLocaleTimeString('zh-CN', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

// 格式化JSON预览
function formatJsonPreview(jsonData: any): string {
  if (!jsonData) return ''
  const str = JSON.stringify(jsonData)
  return str.length > 100 ? str.substring(0, 100) + '...' : str
}

// 打开日志详情
function openLogDetail(log: LogEntry) {
  selectedLog.value = log
  logDetailVisible.value = true
}

// 定位到节点
function locateNode(nodeId: string) {
  if (!nodeId) return

  // 设置激活节点
  workflow.setActiveNodeId(nodeId)

  // 滚动到节点位置
  nextTick(() => {
    const nodeEl = document.querySelector(`[data-id="${nodeId}"]`) as HTMLElement
    if (nodeEl) {
      nodeEl.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      })
      // 高亮显示节点
      nodeEl.style.boxShadow = '0 0 20px rgba(0, 122, 204, 0.8)'
      setTimeout(() => {
        nodeEl.style.boxShadow = ''
      }, 2000)
    }
  })
}

// 暴露方法给外部使用
defineExpose({
  addLog,
  clearLogs
})

// 初始化
onMounted(() => {
  const savedHeight = localStorage.getItem('bottomPanelHeight')
  maxHeight.value = getMaxHeight()
  if (savedHeight) {
    const h = parseInt(savedHeight)
    panelHeight.value = Math.min(maxHeight.value, Math.max(minHeight, h))
  }

  // 恢复水平拖拽偏移
  const savedLeft = localStorage.getItem('bottomPanelLeftDrag')
  const savedRight = localStorage.getItem('bottomPanelRightDrag')
  if (savedLeft) leftDragOffset.value = Math.max(0, parseInt(savedLeft))
  if (savedRight) rightDragOffset.value = Math.max(0, parseInt(savedRight))
  clampHorizontal()

  const handleResize = () => {
    maxHeight.value = getMaxHeight()
    panelHeight.value = Math.min(maxHeight.value, Math.max(minHeight, panelHeight.value))
    clampHorizontal()
  }
  window.addEventListener('resize', handleResize)

  // 添加一些示例日志
  addLog('info', '工作流编辑器已启动', { source: 'System' })
  addLog('debug', '日志面板已加载', { source: 'Debug' })
  addLog('info', '等待工作流执行...', { source: 'System' })

  // 新增：首次挂载后同步当前高度给父组件，避免父组件 paddingBottom 与实际高度不一致
  emit('update:height', panelHeight.value)

  // 卸载时清理
  onUnmounted(() => window.removeEventListener('resize', handleResize))
})
</script>

<style scoped lang="scss">
// CSS变量定义 - 与全局主题保持一致
.bottom-panel {
  --panel-bg: #1F1F1F;
  --panel-border: #333333;
  --panel-text: #cccccc;
  --panel-text-secondary: #999;
  --panel-accent: #007acc;
  --panel-hover-bg: #333;
  --panel-surface: #2B2B2B;
  --panel-surface-hover: #404040;
  --panel-radius: 6px;
  --panel-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--panel-bg);
  border-top: 1px solid var(--panel-border);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), left 0.25s ease, right 0.25s ease;
  box-shadow: var(--panel-shadow);

  &.collapsed {
    transform: translateY(100%);
    box-shadow: none;
    border-top-color: transparent;
  }
}

.resize-handle {
  position: absolute;
  top: -3px;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--panel-border);
  cursor: ns-resize;
  z-index: 1001;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 16px;
  background: var(--panel-bg);
  border-bottom: 1px solid var(--panel-border);
  min-height: 42px;
  gap: 12px;
}

.header-left {
  margin-right: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.panel-title {
  color: var(--panel-text);
  font-size: 13px;
  font-weight: 600;
}

.header-tools {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.search-input {
  flex: 1;
}

:deep(.header-tools .el-select) {
  width: 160px;
}
:deep(.header-tools .el-select .el-input__inner) { font-size: 12px !important; }
:deep(.bottom-panel-level-popper .el-select-dropdown__item) { font-size: 12px !important; }

:deep(.header-tools .el-input__wrapper),
:deep(.header-tools .el-select .el-input__wrapper) {
  height: 28px;
  padding: 4px 10px;
  border-radius: var(--panel-radius);
  background: #2b2b2b !important;
  background-color: #2b2b2b !important;
  border: 1px solid var(--panel-border);
  box-shadow: none;
}
:deep(.el-select__wrapper) { min-height: 28px;
    background: var(--panel-surface);
    box-shadow: none;
    font-size: 12px;
  
  }


:deep(.header-tools .el-select),
:deep(.header-tools .el-input) {
  height: 28px;
  --el-fill-color-blank: #2b2b2b;
  --el-input-bg-color: #2b2b2b;
  --el-input-text-color: var(--panel-text);
  --el-input-border-color: var(--panel-border);
}

:deep(.header-tools .el-input__inner) { background: transparent !important; color: var(--panel-text); }

:deep(.header-tools .el-input__wrapper:hover),
:deep(.header-tools .el-select .el-input__wrapper:hover) {
  border-color: var(--panel-surface-hover);
}

:deep(.header-tools .el-input__wrapper.is-focus),
:deep(.header-tools .el-select.is-focus .el-input__wrapper) {
  border-color: var(--panel-accent);
}

.header-controls {
  flex: 1;
  display: flex;
  justify-content: flex-end;
}

// removed brand-point and header-text styles



.log-time {
  color: var(--panel-text-secondary);
  font-size: 10px;
}

.log-level {
  text-transform: uppercase;
  font-size: 10px;
  font-weight: 600;
}

.log-node {
  color: var(--panel-accent);
  font-size: 10px;
  cursor: pointer;
}

.log-message {
  color: var(--panel-text);
  word-break: break-word;
  line-height: 1.4;
}

.log-json {
  color: #bfbfbf;
  opacity: 0.9;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
}

.log-detail-content {
  color: var(--panel-text);
  font-size: 12px;
}

.detail-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.detail-row label {
  width: 70px;
  color: var(--panel-text-secondary);
}

.node-link {
  color: var(--panel-accent);
  cursor: pointer;
}

.json-display {
  max-height: 50vh;
  overflow: auto;
  background: #1e1e1e;
  padding: 12px;
  border: 1px solid #333;
  border-radius: 6px;
}



.log-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.log-filter {
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  color: var(--panel-text);
  // padding: 6px 10px;
  border-radius: var(--panel-radius);
  font-size: 11px;
  height: 28px;
  min-width: 90px;
  transition: all 0.2s ease;


  &:focus {
    outline: none;
    border-color: var(--panel-accent);
  }

  &:hover {
    border-color: var(--panel-surface-hover);
  }
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid transparent;
  color: var(--panel-text-secondary);
  font-size: 11px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: var(--panel-radius);
  transition: all 0.2s ease;

  &:hover {
    background: var(--panel-hover-bg);
    color: var(--panel-text);
    border-color: var(--panel-border);
  }

  &.active {
    color: var(--panel-accent);
    background: rgba(0, 122, 204, 0.1);
    border-color: var(--panel-accent);
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.header-btn {
  background: none;
  border: 1px solid transparent;
  color: var(--panel-text-secondary);
  cursor: pointer;
  padding: 6px 8px;
  border-radius: var(--panel-radius);
  font-size: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--panel-hover-bg);
    color: var(--panel-text);
    border-color: var(--panel-border);
  }
}

/* 新增：作为下拉触发器的按钮更明显一些 */
.dropdown-trigger {
  width: 28px;
  height: 28px;
  line-height: 1;
  font-size: 16px;
}

/* 菜单图标对齐 */
:deep(.el-dropdown-menu__item) .menu-icon {
  display: inline-flex;
  width: 16px;
  margin-right: 6px;
  align-items: center;
  justify-content: center;
}

.collapse-btn {
  display: none !important;
}

.panel-content {
  flex: 1;
  overflow: hidden;
  background: var(--panel-surface);
}

.logs-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.logs-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;

    &:hover {
      background: rgba(255, 255, 255, 0.16);
    }
  }
}

.log-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: var(--panel-radius);
  font-size: 11px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  margin-bottom: 6px;
  transition: background-color 0.2s ease;
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
}

.log-item:hover {
  background: #333;
}

.log-message {
  flex: 1;
}

.log-level {
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 10px;
}

.log-time {
  margin-left: 8px;
}

.log-item.info .log-level {
  color: #409EFF;
  background: rgba(64, 158, 255, 0.12);
}

.log-item.warn .log-level {
  color: #E6A23C;
  background: rgba(230, 162, 60, 0.12);
}

.log-item.error .log-level {
  color: #F56C6C;
  background: rgba(245, 108, 108, 0.12);
}

.log-item.debug .log-level {
  color: var(--panel-text-secondary);
  background: rgba(255, 255, 255, 0.06);
}

.log-time {
  color: var(--panel-text-secondary);
  font-size: 10px;
  font-weight: 400;
}

.log-level {
  text-transform: uppercase;
  font-size: 10px;
  font-weight: 600;
}

.log-source {
  color: var(--panel-accent);
  font-size: 10px;
  font-weight: 500;
}

.log-message {
  color: var(--panel-text);
  word-break: break-word;
  line-height: 1.4;
  font-weight: 400;
}

.no-logs {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 140px;
  color: var(--panel-text-secondary);
  text-align: center;
}

.no-logs-icon {
  font-size: 28px;
  margin-bottom: 10px;
  opacity: 0.6;
}

.no-logs-text {
  font-size: 12px;
  opacity: 0.8;
}

// 响应式设计优化
@media (max-width: 768px) {
  .panel-header {
    padding: 8px 12px;
    gap: 8px;
    min-height: 38px;
  }

  .header-controls {
    display: none;
  }

  .log-item {
    grid-template-columns: 65px 45px 75px 1fr;
    gap: 8px;
    padding: 6px 10px;
    font-size: 10px;
  }

  .logs-list {
    padding: 8px 12px;
  }

  .header-text {
    gap: 1px;
  }

  .panel-title {
    font-size: 12px;
  }

  .panel-subtitle {
    font-size: 10px;
  }
}

@media (max-width: 480px) {
  .log-item {
    grid-template-columns: 1fr;
    gap: 4px;
    padding: 8px 10px;
  }

  .log-time,
  .log-level,
  .log-source {
    display: inline;
    margin-right: 8px;
  }

  .log-message {
    display: block;
    margin-top: 4px;
    padding-left: 12px;
  }
}
</style>
